import { createVerifiedUserTooltip } from "~/util/userweb";
import { _ } from "~/i18n";
import { node, icon, text } from "~/util/dom";
import { likeButton } from "~/photo/components/like-button";
import { viewCount } from "~/photo/components/view-count";
import { createDeepLTranslate } from "~/ui/deepl";
import { IMedia } from "~/util/providers/photo";
import { CONFIG } from "~/config";
import { generatePoiLinkContainer } from "~/photo/detail/components/poi-link";
import { generateReviewLine } from "~/ui/photo/review";
import { store } from "../globals";
import createSDSIcon from "~/ui/icon";
import { MYMAPS_SHARE } from "~/ui/sds-compatible-icons";

/* Detail fotky */
export default class Description {
	_node: HTMLDivElement;

	_desc: HTMLDivElement;

	_descName: HTMLSpanElement;

	_descContent: HTMLSpanElement;

	_toggleText: HTMLAnchorElement;

	_toggleTextContainer: HTMLDivElement;

	_data: IMedia;

	_my: boolean;

	_clamp: {
		original?: string;
		clamped?: string;
	};

	constructor() {
		this._node = node("div", { className: "description" });
		this._desc = node("div", { className: "desc" });
		this._descName = node("span", { className: "desc-name" });
		this._descContent = node("span", { className: "desc-content" });
		this._toggleText = node("a", { href: "#", className: "show-full-text" }, _("photo.showtext"));
		this._toggleTextContainer = node("div", { className: "options-container" });
		this._data = null;
		this._my = false;
		this._clamp = null;
		this._desc.append(this._descName, this._descContent);

		this._node.addEventListener("click", this, false);
	}

	destroy() {
		this._node.removeEventListener("click", this, false);

		this._node.remove();
		this._node.innerHTML = "";

		this._data = null;
		this._my = false;
		this._clamp = null;
	}

	get node() {
		return this._node;
	}

	set data(data) {
		this._data = data.detail;
		this._my = !!data.my;

		this._build();
	}

	clamp() {
		this._desc.classList.remove("clamp-2");
		this._desc.classList.remove("clamp-3");

		this._toggleText.textContent = _("photo.showtext");
		this._toggleText.className = "show-full-text";

		if (this._descContent.textContent && this._desc.clientHeight > 40) {
			this._desc.classList.add(`clamp-${this._node.offsetWidth < 480 || this._data.review ? 2 : 3}`);
			this._toggleTextContainer.insertBefore(this._toggleText, this._toggleTextContainer.firstChild);
			this._toggleTextContainer.classList.add("clamp");
			this._desc.after(this._toggleTextContainer);
		}
	}

	handleEvent(event) {
		if (event.type === "click") {
			if (event.target.classList.contains("show-full-text")) {
				event.preventDefault();

				const galleryLogData = Mapy.getComponent("gallery").isActive() ? Mapy.getComponent("gallery").logData : {};

				Mapy.getComponent("logger").log("click", Object.assign(galleryLogData, {
					galleryType: galleryLogData.galleryType ? galleryLogData.galleryType : "photomap",
					section: "gallery",
					place: "detail",
					text: _("photo.showtext"),
				}));

				this._desc.className = "desc";

				this._toggleText.textContent = _("photo.hidetext");
				this._toggleText.className = "hide-full-text";
			} else if (event.target.classList.contains("hide-full-text")) {
				event.preventDefault();

				const galleryLogData = Mapy.getComponent("gallery").isActive() ? Mapy.getComponent("gallery").logData : {};

				Mapy.getComponent("logger").log("click", Object.assign(galleryLogData, {
					galleryType: galleryLogData.galleryType ? galleryLogData.galleryType : "photomap",
					section: "gallery",
					place: "detail",
					text: _("photo.hidetext"),
				}));

				this._desc.classList.add(`clamp-${this._node.offsetWidth < 480 || this._data.review ? 2 : 3}`);

				this._toggleText.textContent = _("photo.showtext");
				this._toggleText.className = "show-full-text";
			}
		}
	}

	_build() {
		this._node.innerHTML = "";

		this._node.classList.remove("no-author");
		this._node.classList.remove("my");

		if (this._data.entitySource) {
			this._node.append(generatePoiLinkContainer(this._data));
		}

		if (this._data.review) {
			this._node.append(generateReviewLine(this._data.review));
		}

		const bottom = node("div", { className: "desc-bottom" });
		const buttonBox = node("div", { className: "button" });
		const shareButton = node("button", { className: "share", type: "button", innerHTML: `<span>${_("action.share.title")}</span>` });
		const textToTranslation = [];
		const isWikimedia = !!this._data.wikimedia;

		shareButton.setAttribute("data-log-ignore", "1");
		shareButton.firstChild.before(createSDSIcon({ symbol: MYMAPS_SHARE, className: "icon icon-share" }));

		this._desc.remove();
		this._descName.innerHTML = "";
		this._descContent.innerHTML = "";

		if (isWikimedia) {
			const date = this._data.date
				? new Date(this._data.date)
				: null;
			const dateString = date
				? new Intl.DateTimeFormat(CONFIG.defaultLang, { year: "numeric", month: "long", day: "numeric" }).format(date)
				: "";

			if (dateString) {
				this._desc.innerHTML = dateString;
			}
		} else if (this._data.review) {
			if (this._data.review.text) {
				this._descContent.innerHTML = Mapy.Util.escapeHTML(this._data.review.text);
			}
		} else if (this._data.name || this._data.description) {
			if (this._data.name) {
				this._descName.innerHTML = `${Mapy.Util.escapeTags(this._data.name)}${this._data.description ? "&nbsp;•&nbsp;" : ""}`;
			}

			if (this._data.description) {
				this._descContent.innerHTML += Mapy.Util.escapeHTML(this._data.description);
			}
		}

		if (this._descName.innerHTML || this._descContent.innerHTML) {
			this._node.appendChild(this._desc);

			((this._data.review?.lang && !isWikimedia) || this._data.language) && textToTranslation.push({
				text: this._descContent.innerHTML,
				lang: this._data.review?.lang || this._data.language,
				container: this._descContent,
			});
		}

		if (this._data.author?.userPublicId && this._data.author?.userSource) {
			bottom.appendChild(this._buildAuthorBox(isWikimedia));
		} else {
			bottom.appendChild(node("span", { className: "author" }));
		}

		!this._data.author?.userPublicId && this._node.classList.add("no-author");

		if (this._data.status === "rejected") {
			const approved = node("span", { className: "photo-not-approved", title: _("gallery.notApproved") });

			approved.appendChild(text(_("gallery.reviewError")));
			buttonBox.appendChild(approved);
		} else if (this._data.approved === false) {
			const approved = node("span", { className: "photo-not-approved", title: _("gallery.notApproved") });

			approved.appendChild(icon("time"));
			approved.appendChild(text(_("gallery.notApproved")));

			buttonBox.appendChild(approved);
		} else {
			buttonBox.appendChild(likeButton({
				source: this._data.source,
				id: this._data.id,
				like: this._data.likes.voted,
				likeCount: this._data.likes.count,
			}));

			buttonBox.appendChild(viewCount(this._data.viewCount));
			buttonBox.appendChild(shareButton);
		}

		if (this._my) {
			this._node.classList.add("my");
		}

		const isCurrentUser = this._data.author?.userPublicId === store.getState().user.user.userPublicId;
		const photoHasExif = Array.isArray(this._data.exif) && this._data.exif.length > 0;

		if (!(isCurrentUser && !photoHasExif && !!this._data.review)) {
			buttonBox.appendChild(icon("etc-vertical"));
		}

		// depl preklad
		if (textToTranslation.length) {
			const deepl = createDeepLTranslate(textToTranslation, this._data.review ? "review" : "photo", () => {
				if (this._desc.parentNode) {
					if (this._desc.classList.contains(`clamp-${this._node.offsetWidth < 480 || this._data.review ? 2 : 3}`)) {
						this.clamp();
					}

					const clampButton = this._desc.querySelector("a.show-full-text") as HTMLAnchorElement;

					if (this._desc.closest("div.open") && clampButton) {
						clampButton.click();
					}
				}
			});

			if (deepl) {
				const prevDeepl = this._toggleTextContainer.querySelector(".deepl");

				if (prevDeepl) {
					prevDeepl.remove();
				}

				this._toggleTextContainer.append(deepl);
				this._toggleTextContainer.classList.add("translate");
				this._desc.after(this._toggleTextContainer);
			}
		}

		bottom.appendChild(buttonBox);

		this._node.appendChild(bottom);
	}

	_buildAuthorBox(isWikimedia: boolean) {
		const author = this._data.author;
		const href = `https://${CONFIG.BASE_URL}/?up=${author.userPublicId}${this._data.review || this._data.source === "revi" ? "&upc=reviews" : ""}`;
		const authorBox = CONFIG.isScreenshoter || isWikimedia
			? node("span", { className: "author", title: isWikimedia ? "" : author.name || "" })
			: node("a", { className: "author", href, title: author.name || "" });
		const avatar = node("mapy-avatar", { size: 36, premium: Mapy.CUS.user.premiumCrowns && author.hasPremium ? "mapy" : "none" });
		const avatarImage = node("img", { src: "", alt: "", crossOrigin: "anonymous" });
		const div = node("div");
		const name = node("h4");
		const desc = node("p", {});
		const date = this._data.date ? new Date(this._data.date) : null;
		const dateString = date ? Mapy.Util.dateToLocaleFormatLocal(date) : "";

		authorBox.setAttribute("data-log-ignore", "1");
		isWikimedia && authorBox.classList.add("wikimedia");

		if (isWikimedia) {
			const hasLink = this._data.wikimedia.license_url;
			const authorLink = hasLink
				? node("a", { href: this._data.wikimedia.license_url, target: "_blank", rel: "noopener noreferrer" })
				: node("span");

			authorLink.textContent = this._data.wikimedia.license;
			hasLink && authorLink.append(icon("forward"));
			desc.textContent = `${this._data.wikimedia.author}, `;
			desc.append(authorLink);
		} else if (this._data.review?.user) {
			desc.innerHTML = `${this._data.review?.user.review_count || 0} ${_("userProfile.reviewCount", { count: this._data.review?.user.review_count || 0 })}`;
		} else if (author.mediaCount !== undefined) {
			desc.innerHTML = `${_("photo.author")}&nbsp;•&nbsp;${author.mediaCount} ${_("gallery.stats.photos", author.mediaCount)}`;
		}

		if (dateString && !isWikimedia && !this._data.review) {
			desc.innerHTML += `&nbsp;•&nbsp;${dateString}`;
		}

		if (author.userSource === "rus" && author.avatarUrl) {
			avatarImage.src = `${author.avatarUrl}/36`;
			avatar.appendChild(avatarImage);
		}

		name.appendChild(node("div", {}, Mapy.Util.escapeTags(author.name) || ""));

		// pokud je overeny
		if (author.bankId) {
			const verifiedIcon = icon("verifieduser16");

			name.appendChild(verifiedIcon);
			createVerifiedUserTooltip(verifiedIcon);
		}

		div.appendChild(name);
		div.appendChild(desc);

		!isWikimedia && authorBox.appendChild(avatar);
		authorBox.appendChild(div);

		return authorBox;
	}
}
